// use-spotify-token.jsx — Spotify bearer token via the ha-av token bridge.
//
// Wraps the spotify_token.get service (ha-av custom_component) that
// piggybacks on HA's built-in Spotify integration's OAuth session. The
// panel uses the returned bearer for direct Spotify Web API calls
// (/v1/search, /v1/me/player/recently-played, /v1/browse/...).
//
// Caching: bearer + expires_at live in a useRef so refreshes don't
// re-render the tree. ready/error are useState so MusicShell can react
// to first-load. getToken() returns the cached bearer if it's still
// fresh, otherwise awaits a refresh — call it from every Spotify Web
// API request site (no need to track expiry at the call site).
//
// Refresh policy: preemptive timer at (expires_at - 60s) so concurrent
// in-flight Spotify calls never race a refresh; every 10 s after that until
// HA's own threshold hands back a new token (nextRefreshDelayMs). On HA boot transients
// (spotify_token service raises HomeAssistantError if no spotify config
// entry has finished loading yet) the hook retries with exponential
// backoff per the HANDOFF guidance.
//
// Contract with the bridge service per ha-av/HANDOFF.md "Spotify token
// bridge": call_service with return_response=true, result.response =
// { access_token, expires_at, scope }.

import * as React from 'react';

const { useState, useEffect, useRef, useCallback } = React;

const REFRESH_LEAD_SECONDS = 60;   // refresh this far before expiry
const INSIDE_LEAD_RETRY_SECONDS = 10;  // re-ask cadence when HA hands back a token already inside the lead
const MIN_USABLE_SECONDS = 10;     // getToken serves the cached bearer until this close to expiry
const RETRY_BACKOFFS_MS = [500, 1000, 2000, 4000];  // total ~7.5s of retries
const FAILED_RETRY_MS = 30000;     // long-poll retry after all backoffs fail

// When to fetch next, given the token the bridge just returned. HA's OAuth
// session only refreshes inside its own threshold (CLOCK_OUT_OF_SYNC_MAX_SEC,
// 20 s in Core 2026.10), so a refresh asked for at expiry - 60 s gets the
// same token back with 20-60 s left. Rescheduling that at 0 ms spun the
// timer at WebSocket speed for the whole 40 s window: ~1,300 calls per burst,
// 2.7 bursts a day (Green recorder, 18 Oct - 1 Nov 2026). Inside the lead,
// wait a fixed step instead and let HA's threshold arrive.
export function nextRefreshDelayMs(expiresAt, nowSeconds) {
  const remaining = expiresAt - nowSeconds;
  if (remaining > REFRESH_LEAD_SECONDS) {
    return (remaining - REFRESH_LEAD_SECONDS) * 1000;
  }
  return INSIDE_LEAD_RETRY_SECONDS * 1000;
}

// Whether a cached bearer is still good to hand to a Spotify Web API call.
// Deliberately looser than REFRESH_LEAD_SECONDS: the timer chain owns
// refreshing, and a token with 20-60 s left is valid for a sub-second call.
export function cachedTokenUsable(cached, nowSeconds) {
  return !!cached && cached.expiresAt - nowSeconds > MIN_USABLE_SECONDS;
}

export function useSpotifyToken(hass) {
  // CRITICAL: `hass` gets a new object reference on every HA state-change
  // event (which fires constantly). Storing it in a ref keeps fetchToken
  // and getToken stable across renders — without this, every state event
  // cascades into a new getToken identity, causing useFetched consumers
  // (e.g. Recently Played carousel) to reset to null and refetch in a
  // visible flicker loop. The ref pattern means the effect below only
  // runs on hass present/absent transitions, not on every update.
  const hassRef = useRef(hass);
  useEffect(() => { hassRef.current = hass; }, [hass]);

  // Bearer cache — lives in a ref so refreshes don't re-render anything
  // unless ready/error flip (which only happens on first load + on
  // recovery from a sustained error).
  const cacheRef = useRef(null);    // { token, expiresAt, scope }
  const inflightRef = useRef(null); // dedupe concurrent refresh calls
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  // Single source of truth for "fetch a fresh token." Dedupes concurrent
  // callers (e.g. multiple Spotify Web API requests racing a refresh)
  // and retries with backoff on transients. Reads hass from the ref so
  // its identity stays stable.
  const fetchToken = useCallback(async () => {
    const hass = hassRef.current;
    if (!hass?.callWS) throw new Error('hass.callWS not ready');
    if (inflightRef.current) return inflightRef.current;

    inflightRef.current = (async () => {
      let lastError = null;
      for (let attempt = 0; attempt <= RETRY_BACKOFFS_MS.length; attempt++) {
        if (attempt > 0) {
          await new Promise(r => setTimeout(r, RETRY_BACKOFFS_MS[attempt - 1]));
        }
        try {
          const result = await hass.callWS({
            type: 'call_service',
            domain: 'spotify_token',
            service: 'get',
            return_response: true,
          });
          // HA WebSocket returns the service_response under `.response`.
          // Be defensive: older HA versions may have used a different
          // key, so accept either spelling.
          const resp = result?.response || result?.service_response || result;
          if (!resp?.access_token) {
            throw new Error('spotify_token.get returned no access_token');
          }
          cacheRef.current = {
            token: resp.access_token,
            expiresAt: resp.expires_at,  // unix epoch seconds
            scope: resp.scope || '',
          };
          return cacheRef.current;
        } catch (e) {
          lastError = e;
          // fall through to next backoff
        }
      }
      throw lastError || new Error('spotify_token.get failed after retries');
    })();

    try {
      return await inflightRef.current;
    } finally {
      inflightRef.current = null;
    }
  }, []);  // STABLE: empty deps — hass read via hassRef.current.

  // Public API: getToken({ force }) — returns cached bearer if still
  // fresh, otherwise refreshes. Pass { force: true } to bypass the
  // cache (used by the Spotify Web API client when a 401 indicates
  // the cached bearer is no longer accepted).
  const getToken = useCallback(async ({ force = false } = {}) => {
    if (!force) {
      const cached = cacheRef.current;
      if (cachedTokenUsable(cached, Date.now() / 1000)) {
        return cached.token;
      }
    }
    const refreshed = await fetchToken();
    return refreshed.token;
  }, [fetchToken]);

  // Preemptive load on mount + scheduled refresh chain. Depends on a
  // boolean (hass present vs absent) rather than hass identity so this
  // effect only runs on the present/absent transition, not on every
  // state-change re-render.
  const hasHass = !!hass;
  useEffect(() => {
    if (!hasHass) return;
    let cancelled = false;
    let timer = null;

    const load = async () => {
      try {
        await fetchToken();
        if (cancelled) return;
        setReady(true);
        setError(null);
        // Schedule next refresh at (expiresAt - REFRESH_LEAD_SECONDS), or a
        // fixed step later if HA returned a token already inside the lead.
        const cached = cacheRef.current;
        if (cached) {
          timer = setTimeout(load, nextRefreshDelayMs(cached.expiresAt, Date.now() / 1000));
        }
      } catch (e) {
        if (cancelled) return;
        setError(e);
        // Long-poll retry after a sustained failure (e.g. spotify
        // integration not configured yet); avoids hammering HA.
        timer = setTimeout(load, FAILED_RETRY_MS);
      }
    };
    load();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [hasHass, fetchToken]);  // fetchToken is now stable (empty deps).

  return {
    getToken,
    ready,
    error,
    // Debug-only: seconds until current cached bearer expires. Exposed
    // for the PR 2 step-2 smoke-test footer rendering; will be removed
    // once the hook is verified end-to-end and the carousels are wired.
    expiresIn: () => {
      const c = cacheRef.current;
      if (!c) return null;
      return Math.max(0, Math.round(c.expiresAt - Date.now() / 1000));
    },
  };
}
